'use client';

import Script from 'next/script';
import React, { useEffect, useRef } from 'react';
import { CaptionWithMB, Subtitle, H2 } from './styleConstants';
import { Container } from '..';
import { CustomImage as Image } from '..';
import styles from './VisionSection.module.css';

const WIDGET_SCRIPT_SRC =
  'https://cdn.jsdelivr.net/gh/hiunicornstudio/unicornstudio.js@v1.5.3/dist/unicornStudio.umd.js';

declare global {
  interface Window {
    UnicornStudio?: {
      isInitialized?: boolean;
      init?: () => void;
    };
  }
}

export const VisionSection = ({ className }: { className?: string }) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const projectId = 'Xg0TdNHOjlUSqZTq1juh';

  useEffect(() => {
    if (typeof window === 'undefined') {
      return;
    }

    let timeoutId: ReturnType<typeof setTimeout> | undefined;

    const tryInit = () => {
      const container = document.querySelector(
        `[data-us-project="${projectId}"]`,
      );

      if (
        container &&
        window.UnicornStudio &&
        typeof window.UnicornStudio.init === 'function'
      ) {
        window.UnicornStudio.init();
        window.UnicornStudio.isInitialized = true;
        return;
      }

      timeoutId = setTimeout(tryInit, 50);
    };

    tryInit();

    return () => {
      if (timeoutId) {
        clearTimeout(timeoutId);
      }
    };
  }, [projectId]);

  const handleScriptLoad = () => {
    if (!containerRef.current || !window.UnicornStudio?.init) {
      return;
    }
    window.UnicornStudio.init();
    window.UnicornStudio.isInitialized = true;
  };

  return (
    <section className={`${styles.root}${className ? ` ${className}` : ''}`}>
      <Container className={styles.container}>
        <div className="text-content">
          <CaptionWithMB>Solution</CaptionWithMB>
          <H2 style={{ marginBottom: '12px' }}>
            One command center for all your data
          </H2>
          <Subtitle style={{ margin: '0 auto' }}>
            A unified enterprise-grade dashboard for teams and clinicians to
            track sleep, recovery, movement, cycles, and long-term health using
            continuous biomarkers.
          </Subtitle>
        </div>
        <div className={styles.mediaContainer}>
          <div className={styles.unicornEmbed}>
            <Script
              src={WIDGET_SCRIPT_SRC}
              strategy="lazyOnload"
              onLoad={handleScriptLoad}
            />
            <div
              ref={containerRef}
              data-us-project={projectId}
              style={{
                width: '100%',
                height: '100%',
              }}
            />
          </div>
          <div className={styles.fallbackImage}>
            <Image
              src="web_v2/partners/vision-db.png/w_1920"
              width="1200"
              height="780"
              alt="Ultrahuman Vision Dashboard"
            />
          </div>
        </div>
      </Container>
    </section>
  );
};
